import React, { useState } from 'react';
import { Users, TrendingUp, Clock, Shield } from 'lucide-react';
const PersonaBenefits = () => {
  const [activeTab, setActiveTab] = useState('business-owner');
  const tabs = [{
    id: 'business-owner',
    label: 'Business Owner',
    icon: Users
  }, {
    id: 'finance-manager',
    label: 'Finance Manager',
    icon: TrendingUp
  }, {
    id: 'operations',
    label: 'Operations',
    icon: Clock
  }];
  const tabContent = {
    'business-owner': {
      title: 'Focus on Growing Your Business, Not Paperwork',
      benefits: ['Free up 12+ hours per month spent on bill management', 'Improve cash flow with 30-day payment terms', 'Eliminate late fees and maintain vendor relationships', 'Get real-time visibility into company spending'],
      testimonial: {
        quote: 'Settl has transformed how we manage our finances. The 30-day payment window gives us breathing room, and my finance team is saving hours every week.',
        name: 'Sarah Tan',
        position: 'CEO, TechVision Malaysia',
        image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80'
      }
    },
    'finance-manager': {
      title: 'Streamline Your Finance Operations',
      benefits: ['Centralize all bill payments in one dashboard', 'Automated reconciliation and reporting', 'Reduce errors and duplicate payments', 'Export data to your accounting software'],
      testimonial: {
        quote: "The automation is incredible. We've reduced payment processing time by 80% and eliminated errors. The monthly consolidated statement makes reconciliation a breeze.",
        name: 'Ahmad Rizal',
        position: 'Finance Director, MedSupply KL',
        image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80'
      }
    },
    operations: {
      title: 'Keep Operations Running Smoothly',
      benefits: ['Never miss a critical vendor payment', 'Maintain service continuity with suppliers', 'Streamline approval workflows', 'Reduce administrative overhead'],
      testimonial: {
        quote: "Since implementing Settl, we've never had a service interruption due to missed payments. The approval process is quick and the automated tracking keeps everything in order.",
        name: 'Mei Ling',
        position: 'Operations Manager, FoodGroup Malaysia',
        image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80'
      }
    }
  };
  const activeContent = tabContent[activeTab];
  return <section className="section bg-light-sky" data-id="element-152">
      <div className="container" data-id="element-153">
        <div className="text-center mb-16" data-id="element-154">
          <h2 className="font-outfit font-bold text-3xl md:text-4xl mb-6" data-id="element-155">
            Benefits for Your Entire Team
          </h2>
          <p className="text-slate-grey text-lg max-w-3xl mx-auto" data-id="element-156">
            See how Settl helps different roles in your organization save time
            and reduce stress.
          </p>
        </div>
        {/* Tab Navigation */}
        <div className="flex flex-col md:flex-row justify-center mb-12" data-id="element-157">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row" data-id="element-158">
            {tabs.map(tab => {
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center py-4 px-6 md:px-8 transition-colors ${activeTab === tab.id ? 'bg-settl-blue text-white' : 'bg-white text-charcoal hover:bg-gray-50'}`} data-id="element-159">
                  <Icon className="h-5 w-5 mr-2" data-id="element-160" />
                  <span className="font-medium" data-id="element-161">{tab.label}</span>
                </button>;
          })}
          </div>
        </div>
        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-id="element-162">
          {/* Benefits */}
          <div className="lg:col-span-3 bg-white rounded-xl shadow-sm p-8" data-id="element-163">
            <h3 className="font-outfit font-semibold text-2xl mb-6" data-id="element-164">
              {activeContent.title}
            </h3>
            <ul className="space-y-4" data-id="element-165">
              {activeContent.benefits.map((benefit, index) => <li key={index} className="flex items-start" data-id="element-166">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-success-green bg-opacity-20 flex items-center justify-center mt-0.5 mr-3" data-id="element-167">
                    <Shield className="h-3.5 w-3.5 text-success-green" data-id="element-168" />
                  </div>
                  <span className="text-slate-grey" data-id="element-169">{benefit}</span>
                </li>)}
            </ul>
            <div className="mt-8" data-id="element-170">
              <a href="https://wa.me/60127743934/?text=I%20am%20interested%20in%20Settl" className="btn-primary" data-id="element-171">
                See How It Works
              </a>
            </div>
          </div>
          {/* Testimonial */}
          <div className="lg:col-span-2" data-id="element-172">
            <div className="bg-white rounded-xl shadow-sm p-8 h-full flex flex-col" data-id="element-173">
              <div className="mb-6" data-id="element-174">
                <svg width="42" height="30" viewBox="0 0 42 30" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-175">
                  <path d="M11.4 0C5.1 0 0 5.1 0 11.4C0 17.7 5.1 22.8 11.4 22.8C17.7 22.8 22.8 17.7 22.8 11.4L22.8 0H11.4ZM30.6 0C24.3 0 19.2 5.1 19.2 11.4C19.2 17.7 24.3 22.8 30.6 22.8C36.9 22.8 42 17.7 42 11.4L42 0H30.6Z" fill="#3B82F6" fillOpacity="0.2" data-id="element-176" />
                </svg>
              </div>
              <p className="text-lg text-slate-grey italic mb-6 flex-grow" data-id="element-177">
                "{activeContent.testimonial.quote}"
              </p>
              <div className="flex items-center" data-id="element-178">
                <img src={activeContent.testimonial.image} alt={activeContent.testimonial.name} className="w-12 h-12 rounded-full mr-4 object-cover" data-id="element-179" />
                <div data-id="element-180">
                  <div className="font-medium text-charcoal" data-id="element-181">
                    {activeContent.testimonial.name}
                  </div>
                  <div className="text-sm text-slate-grey" data-id="element-182">
                    {activeContent.testimonial.position}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default PersonaBenefits;